import { api } from '../api/instance.ts';
import type { Me, Schemas } from '../api/types.ts';
import { services } from '../services/index.ts';
import { useAccount } from './store.ts';

/**
 * Beta testers and demo accounts (server: `/v1/beta/redeem`, `me.beta`, `user.demo`).
 */
export type BetaGrant = Schemas['BetaGrant'];

/**
 * Canonical form of a typed beta code: uppercase, spaces removed; "beta7kq2mx9p" and
 * "BETA-7KQ2-MX9P" both become "BETA-7KQ2-MX9P" (the server ignores dashes and case anyway).
 */
export function normalizeBetaCode(text: string): string {
  const raw = text.toUpperCase().replace(/[^A-Z0-9]/g, '');
  const m = /^(BETA)([A-Z0-9]{4})([A-Z0-9]{4})$/.exec(raw);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  return text.toUpperCase().replace(/\s+/g, '');
}

/** Whole days until the beta ends (rounded up; 0 when over). */
export function betaDaysLeft(beta: { endsAt: string } | null | undefined, now = Date.now()): number | null {
  if (!beta) return null;
  const end = Date.parse(beta.endsAt);
  if (!Number.isFinite(end)) return null;
  return Math.max(0, Math.ceil((end - now) / 86_400_000));
}

/** "Nov 18" (or "Nov 18, 2027" in another year). */
export function shortDate(iso: string, now = new Date()): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
}

export const isDemo = (me: Me | null | undefined): boolean =>
  (me?.user as { demo?: boolean } | undefined)?.demo === true;

/** The signed-in account is the seeded demo account: billing actions are hidden or disabled. */
export const useIsDemo = () => useAccount((s) => isDemo(s.me));

export const DEMO_BILLING_NOTE = 'Billing is turned off for this demo account.';

/** Redeems a code, then refreshes /v1/me. Throws ApiError (see apiMessage for the beta codes). */
export async function redeemBetaCode(text: string): Promise<BetaGrant> {
  const grant = await api.redeemBeta(normalizeBetaCode(text));
  await services.account.refreshMe();
  return grant;
}
